import React from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";

const ERROR = "#d92d20";

/**
//    * @component  FormFieldBlock
//    * @description E5 design form field: bold label with *, optional n/max counter (amber at the limit),
//    * the input passed as children, then helper text and the inline error directly below.
//    * @returns form field UI
//    */
function FormFieldBlock(props) {
  const { label, required, value, max, showCounter, error, helper, fieldId, locked, children } = props;
  const length = value ? String(value).length : 0;

  return (
    <Box
      sx={{
        // the older modal SCSS (.modalCardBox .addEditZoneCard .formFieldWrapInner input) is more
        // specific and sets 28px inputs at 50% opacity and capitalised text, so the design values win with !important.
        // Text inputs and selects only: checkboxes (Business Rules) keep their own look.
        textTransform: "none",
        lineHeight: 1.4,
        "& input:not([type=checkbox]):not([type=radio]), & select": {
          width: "100% !important",
          boxSizing: "border-box",
          height: "34px !important",
          border: `1px solid ${error ? ERROR : "#ccc"} !important`,
          borderRadius: "8px !important",
          padding: "0 10px !important",
          fontSize: "12px !important",
          lineHeight: "normal !important",
          backgroundColor: "#fff !important",
          color: "#202020 !important",
          opacity: "1 !important",
          textTransform: "none",
        },
        "& input:not([type=checkbox]):not([type=radio]).fieldError, & select.fieldError": {
          borderColor: `${ERROR} !important`,
        },
        "& input:not([type=checkbox]):not([type=radio]):disabled": {
          backgroundColor: "#f2f2f4 !important",
          color: "#666 !important",
        },
        "& input::placeholder": { color: "#999", fontSize: "12px" },
        "& .formRow, & .formFieldWrap, & .formFieldWrapInner, & .formFieldWrapError": {
          width: "100%",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          mb: "6px",
        }}
      >
        <label htmlFor={fieldId} style={{ fontWeight: 700, fontSize: 12, color: "#202020" }}>
          {label}
          {required ? " *" : ""}
        </label>
        {showCounter && max ? (
          <Box
            component="span"
            data-testid={`${fieldId}-counter`}
            sx={{ fontSize: 10, color: length >= max ? "#b54708" : "#666" }}
          >
            {length}/{max}
          </Box>
        ) : null}
      </Box>
      {locked ? (
        <Box sx={{ position: "relative", "& input:not([type=checkbox])": { paddingRight: "36px !important" } }}>
          {children}
          <Box
            component="span"
            data-testid={`${fieldId}-lock`}
            title={helper}
            sx={{ position: "absolute", right: 10, top: 9, color: "#8a8a8a", fontSize: 11 }}
          >
            <FontAwesomeIcon icon={faLock} />
          </Box>
        </Box>
      ) : (
        children
      )}
      {helper ? (
        <Box sx={{ mt: "6px", fontSize: 11, color: "#666" }}>{helper}</Box>
      ) : null}
      {error ? (
        <Box
          role="alert"
          id={`${fieldId}-error`}
          sx={{
            mt: "6px",
            fontSize: 11,
            color: ERROR,
            display: "flex",
            gap: "6px",
            alignItems: "flex-start",
          }}
        >
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              flexShrink: 0,
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: ERROR,
              color: "#fff",
              fontSize: 9,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mt: "2px",
            }}
          >
            !
          </Box>
          {error}
        </Box>
      ) : null}
    </Box>
  );
}

FormFieldBlock.propTypes = {
  label: PropTypes.string.isRequired,
  fieldId: PropTypes.string.isRequired,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  max: PropTypes.number,
  showCounter: PropTypes.bool,
  error: PropTypes.string,
  helper: PropTypes.string,
  locked: PropTypes.bool,
};

FormFieldBlock.defaultProps = {
  required: false,
  showCounter: false,
  error: "",
  helper: "",
  locked: false,
};

export default FormFieldBlock;
